import React, { memo } from 'react'
import Image from '@v4/components/common/Image'
import { Box, Grid } from '@mui/material'
import EditableHTML from '@v4/components/common/EditableHTML'
import ActionButton from '@v4/components/common/Buttons/ActionButton'
import PropTypes from 'prop-types'
import PageMetaData from '@v4/utils/data/PageMetaData'
import EditableContainer from '@v4/components/common/EditableContainer'
import { CONTENT_WIDTH } from './Content.const'
import { useContent } from './Content.hooks'

const Content = props => {
	const [
		{
			classes,
			cx,
			textShadow,
			text,
			textConfig,
			buttonText,
			contentWidth,
			buttonLink,
			buttonAction,
			extraShortcuts,
			textEditable,
			buttonTextConfig,
			buttonTextEditable,
			buttonTextKey,
			textKey,
			mobileImage,
			desktopImage,
			withMobileImage,
			withBgDesktopImage,
			withBgMobileImage,
			sx,
			sxWhite,
		},
		{
			handleTextClick,
			handleTextChange,
			handleButtonClick,
			handleButtonTextChange,
			handleClick,
			handleMobileImageClick,
			handleDesktopImageClick,
		},
	] = useContent(props)

	return (
		<>
			{withBgDesktopImage && (
				<Image
					className={cx(classes.background, classes.backgroundDesktop, 'banner1-bgImage')}
					metaData={desktopImage}
					onClick={handleDesktopImageClick}
					isBackground
				/>
			)}
			{withBgMobileImage && (
				<Image
					className={cx(classes.background, classes.backgroundMobile, 'banner1-bgImage')}
					metaData={mobileImage?.image ? mobileImage : desktopImage}
					onClick={mobileImage?.image ? handleMobileImageClick : handleDesktopImageClick}
					isBackground
				/>
			)}
			<Grid className={classes.root} container onClick={handleClick}>
				<Grid
					className={classes.grid}
					size={{
						md: CONTENT_WIDTH[contentWidth],
						xs: 12,
					}}
				>
					<div className={cx(classes.content, 'banner1-content')}>
						<EditableContainer
							visible={!!text}
							white={withBgDesktopImage}
							fieldConfig={textConfig}
							onAdd={handleTextChange}
							editable={textEditable}
							sx={sxWhite}
						>
							<Box>
								<EditableHTML
									key={textKey}
									className={classes.text}
									gutterBottom
									white={withBgDesktopImage}
									html={text}
									onClick={handleTextClick}
									extraShortcuts={extraShortcuts}
									onChange={handleTextChange}
									editable={textEditable}
									shadow={withBgDesktopImage}
									textShadow={textShadow}
									sx={sx}
								/>
							</Box>
						</EditableContainer>
						<EditableContainer
							visible={!!buttonText}
							white={withBgDesktopImage}
							fieldConfig={buttonTextConfig}
							onAdd={handleButtonTextChange}
							editable={buttonTextEditable}
							defaultValue={buttonTextConfig?.default}
							label="Button"
							wrapper={({ children }) => (
								<Box
									className={cx(classes.actionContainer, 'banner1-action-container')}
									display="flex"
									justifyContent="center"
									mt={4}
								>
									{children}
								</Box>
							)}
							sx={sxWhite}
						>
							<ActionButton
								key={buttonTextKey}
								action={buttonAction}
								href={buttonLink}
								className={classes.button}
								onClick={handleButtonClick}
								editable={buttonTextEditable}
								onChange={handleButtonTextChange}
							>
								{buttonText}
							</ActionButton>
						</EditableContainer>
					</div>
				</Grid>
				{withMobileImage && (
					<Grid className={cx(classes.mobileImageGrid, 'banner1-image-grid')} size={12}>
						<Box className={classes.mobileImageWrapper}>
							<Image
								className={cx(classes.mobileImage, 'banner1-image-mobile')}
								metaData={mobileImage?.image ? mobileImage : desktopImage}
								onClick={mobileImage?.image ? handleMobileImageClick : handleDesktopImageClick}
							/>
						</Box>
					</Grid>
				)}
			</Grid>
		</>
	)
}

Content.propTypes = {
	desktopImage: PropTypes.instanceOf(PageMetaData),
	mobileImage: PropTypes.instanceOf(PageMetaData),
	buttonAction: PropTypes.string,
	buttonLink: PropTypes.string,
	buttonText: PropTypes.string,
	buttonTextKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	classes: PropTypes.shape({
		root: PropTypes.string,
		grid: PropTypes.string,
		text: PropTypes.string,
		actionContainer: PropTypes.string,
	}),
	contentWidth: PropTypes.string,
	onButtonClick: PropTypes.func,
	onImageClick: PropTypes.func,
	onMobileImageClick: PropTypes.func,
	onTextClick: PropTypes.func,
	onTextChange: PropTypes.func,
	onButtonTextChange: PropTypes.func,
	text: PropTypes.string,
	extraShortcuts: PropTypes.objectOf(PropTypes.string),
	opacity: PropTypes.number,
	mobileOpacity: PropTypes.number,
	textEditable: PropTypes.bool,
	textConfig: PropTypes.object,
	textKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	buttonTextEditable: PropTypes.bool,
	buttonTextConfig: PropTypes.object,
	backgroundImagePosition: PropTypes.string,
	mobileImagePosition: PropTypes.string,
	direction: PropTypes.string,
	mobileAlignment: PropTypes.string,
	mobileButtonWidth: PropTypes.string,
	onClick: PropTypes.func,
}

export default memo(Content)
